import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import FinancialYearService from "../../../services/FinancialYearService";
import BudgetSummaryService from "../../../services/BudgetSummaryService";

const BudgetSummary = () => {
  const [list, setList] = useState([]);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [form] = Form.useForm();

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await BudgetSummaryService.getBudgetSummaries();
      setList(res.data || []);
    } catch (err) {
      console.error(err);
      message.error("Failed to load budget summaries");
    } finally {
      setLoading(false);
    }
  };

  const fetchYears = async () => {
    try {
      const res = await FinancialYearService.getFinancialYears();
      setYears(res.data || []);
    } catch (err) {
      console.error(err);
      message.error("Failed to load financial years");
    }
  };

  useEffect(() => {
    fetch();
    fetchYears();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      year: record.year,
      total_budget: record.total_budget,
      total_income: record.total_income,
      total_expense: record.total_expense,
      balance: record.balance,
      language: record.language,
    });
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete budget summary",
      content: "Are you sure?",
      async onOk() {
        try {
          await BudgetSummaryService.deleteBudgetSummary(id);
          message.success("Deleted");
          fetch();
        } catch (err) {
          console.error(err);
          message.error("Delete failed");
        }
      },
    });
  };

  const handleView = (record) => {
    setViewing(record);
    setIsViewOpen(true);
  };

  const onFinish = async (values) => {
    const payload = {
      year: values.year, // ID of the financial year
      total_budget: values.total_budget || "",
      total_income: values.total_income || "",
      total_expense: values.total_expense || "",
      balance: values.balance || "",
      language: values.language || "",
    };

    setLoading(true);
    try {
      if (editing) {
        await BudgetSummaryService.updateBudgetSummary(editing.id, payload);
        message.success("Updated");
      } else {
        await BudgetSummaryService.createBudgetSummary(payload);
        message.success("Created");
      }
      setIsModalOpen(false);
      form.resetFields();
      fetch();
    } catch (err) {
      console.error(err);
      message.error("Save failed");
    } finally {
      setLoading(false);
    }
  };

  const getYearName = useCallback(
    (id) => {
      const y = years.find((x) => x.id === id);
      const yearName = y ? y.name || y.year_name || id : id;
      return yearName ? `FY ${yearName}` : "-";
    },
    [years]
  );

  // Group data by financial year
  const groupedData = useMemo(() => {
    const groups = {};
    list.forEach((item) => {
      const fyId = item.year;
      if (!groups[fyId]) {
        groups[fyId] = [];
      }
      groups[fyId].push(item);
    });
    return Object.entries(groups).map(([fyId, items]) => ({
      key: `fy-${fyId}`,
      fyId: Number(fyId),
      fyName: getYearName(Number(fyId)),
      items,
      totalBudget: items.reduce(
        (sum, i) => sum + (Number(i.total_budget) || 0),
        0
      ),
      totalExpense: items.reduce(
        (sum, i) => sum + (Number(i.total_expense) || 0),
        0
      ),
    }));
  }, [list, getYearName]);

  const columns = [
    { title: "Total Budget", dataIndex: "total_budget", key: "total_budget" },
    { title: "Total Income", dataIndex: "total_income", key: "total_income" },
    {
      title: "Total Expense",
      dataIndex: "total_expense",
      key: "total_expense",
    },
    { title: "Balance", dataIndex: "balance", key: "balance" },
    { title: "Language", dataIndex: "language", key: "language" },
    {
      title: "Actions",
      key: "actions",
      render: (_, r) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleView(r)}
          >
            View
          </Button>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openEdit(r)}
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleDelete(r.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 12,
          gap: 12,
        }}
      >
        <h3 style={{ margin: 0 }}>Budget Summaries</h3>
        <div style={{ flexShrink: 0 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add
          </Button>
        </div>
      </div>

      {groupedData.length === 0 ? (
        <Table columns={columns} dataSource={[]} loading={loading} />
      ) : (
        groupedData.map((group) => (
          <div key={group.key} style={{ marginBottom: 24 }}>
            <div
              style={{
                background: "linear-gradient(90deg, #001529 0%, #042035 100%)",
                padding: "12px 16px",
                borderRadius: "8px 8px 0 0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ color: "white", fontWeight: 600, fontSize: 16 }}>
                {group.fyName}
              </span>
              <span style={{ color: "rgba(255,255,255,0.9)", fontSize: 14 }}>
                Total Budget: ₹{group.totalBudget.toLocaleString("en-IN")} |
                Total Expense: ₹{group.totalExpense.toLocaleString("en-IN")}
              </span>
            </div>
            <Table
              columns={columns}
              dataSource={group.items}
              rowKey="id"
              loading={loading}
              pagination={false}
              size="small"
              style={{ borderRadius: "0 0 8px 8px", overflow: "hidden" }}
            />
          </div>
        ))
      )}

      <Modal
        title={editing ? "Edit Budget Summary" : "Add budget summary"}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="year"
            label="Financial Year"
            rules={[{ required: true, message: "Select year" }]}
          >
            <Select
              showSearch
              placeholder="Select year"
              options={years.map((y) => ({
                label: `FY ${y.name || y.year_name}`,
                value: y.id,
              }))}
            />
          </Form.Item>
          <Form.Item name="total_budget" label="Total Budget">
            <Input />
          </Form.Item>
          <Form.Item name="total_income" label="Total Income">
            <Input />
          </Form.Item>
          <Form.Item name="total_expense" label="Total Expense">
            <Input />
          </Form.Item>
          <Form.Item name="balance" label="Balance">
            <Input />
          </Form.Item>
          <Form.Item name="language" label="Language" initialValue="Marathi">
            <Select
              allowClear
              options={[
                { label: "English", value: "English" },
                { label: "Marathi", value: "Marathi" },
                { label: "Hindi", value: "Hindi" },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              {editing ? "Update" : "Create"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Budget Summary"
        open={isViewOpen}
        onCancel={() => setIsViewOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewOpen(false)}>
            Close
          </Button>,
        ]}
      >
        {viewing && (
          <div>
            <p>
              <strong>Year:</strong> {getYearName(viewing.year)}
            </p>
            <p>
              <strong>Total Budget:</strong> {viewing.total_budget}
            </p>
            <p>
              <strong>Total Income:</strong> {viewing.total_income}
            </p>
            <p>
              <strong>Total Expense:</strong> {viewing.total_expense}
            </p>
            <p>
              <strong>Balance:</strong> {viewing.balance}
            </p>
            <p>
              <strong>Language:</strong> {viewing.language}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BudgetSummary;
